const API_URL = 'https://qy3psyg36h.execute-api.us-east-2.amazonaws.com/prod/register';

// Simple date formatter function that won't have timezone issues
const formatSimpleDate = (dateString: string): string => {
  // Split the date string (YYYY-MM-DD)
  const [year, month, day] = dateString.split('-').map(num => parseInt(num, 10));
  
  // Get day of week (0-6, where 0 is Sunday)
  // Create date using UTC to avoid timezone shifts
  const date = new Date(Date.UTC(year, month - 1, day));
  const dayOfWeek = date.getUTCDay();
  
  // Day names array
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  
  // Format as "Tuesday 5/27/25"
  const shortYear = year.toString().slice(2, 4);
  return `${dayNames[dayOfWeek]} ${month}/${day}/${shortYear}`;
};

// Interface for meal tag (preferences/suggestions)
export interface MealTag {
  name: string;
  specifics: string;
}

// Interface for current meal sign-up
export interface CurrentSignUp {
  type: 'homemade' | 'restaurant';
  name: string;
  actualMealName: string;
  date: string;
  gifterEmail: string;
  gifterId: string;
  gifterName: string;
  gifterPhone: string;
  isClaimed: boolean;
  specifics: string;
  timeWindow: {
    start: string;
    end: string;
  };
}

// Interface for user data
export interface UserData {
  parentName1: string;
  parentName2: string;
  parentNames: string;
  dueDate: string;
  email: string;
  address: {
    street: string;
    street2: string;
    city: string;
    state: string;
    zip: string;
  };
  phoneNumber: string;
  adminContact: {
    name: string;
    relationship: string;
    email: string;
    phone: string;
  };
  mealTags: MealTag[];
  currentSignUps: CurrentSignUp[];
  dislikes: string[];
  dietaryRestrictions: string;
  additionalNotes: string;
  defaultTimeWindow: {
    start: string;
    end: string;
  };
  mealDays: number;
  numberOfPeople: number;
  numberOfAdults: number;
  numberOfKids: number;
  adminPassword: string;
}

// Interface for registry data
export interface RegistryData {
  parentName1: string;
  parentName2: string;
  parentNames: string;
  dueDate: string;
  email: string;
  address: {
    street: string;
    street2: string;
    city: string;
    state: string;
    zip: string;
  };
  phoneNumber: string;
  adminContact: {
    name: string;
    relationship: string;
    email: string;
    phone: string;
  };
  mealTags: MealTag[];
  currentSignUps: CurrentSignUp[];
  dislikes: string[];
  dietaryRestrictions: string;
  additionalNotes: string;
  defaultTimeWindow: {
    start: string;
    end: string;
  };
  mealDays: number;
  numberOfPeople: number;
  numberOfAdults: number;
  numberOfKids: number;
  adminPassword: string;
}

// Interface for meal slot
interface MealSlot {
  slotId: string;
  registryId: string;
  dayIndex: number;
  date: string;
  mealType: 'restaurant' | 'homemade' | 'gifter_choice';
  mealName: string;
  specifics: string;
  isFilled: boolean;
  gifterId?: string;
  gifterName?: string;
  gifterEmail?: string;
  gifterPhone?: string;
  actualMealName?: string; // What the gifter actually brings
  timeWindow: {
    start: string;
    end: string;
  };
}

export const sendRegistryActivationNotifications = async (registryId: string) => {
  try {
    const result = await makeApiCall('sendRegistryActivationNotifications', { registryId });
    
    if (!result.success) {
      throw new Error('Failed to send notifications');
    }
    
    // Backend now handles all email sending
    console.log(`Registry activation notifications sent for registry ${registryId}`);
    
  } catch (error) {
    console.error('Error sending registry activation notifications:', error);
    throw error;
  }
};

// Fix the API key issue first:
const makeApiCall = async (operation: string, data: any = {}) => {
  const apiKey = process.env.REACT_APP_API_KEY_REGISTRY || '';
  
  const response = await fetch(API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey
    },
    body: JSON.stringify({
      operation,
      ...data
    })
  });
  
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
  }
  
  const responseData = await response.json();
  
  // Parse the body since Lambda uses API Gateway proxy integration
  let parsedData;
  if (responseData.body) {
    parsedData = JSON.parse(responseData.body);
  } else {
    parsedData = responseData;
  }
  
  return parsedData;
};

// Replace cancelMealSignup function:
export const cancelMealSignup = async (registryId: string, gifterId: string): Promise<boolean> => {
  try {
    const result = await makeApiCall('cancelMealSignup', { registryId, gifterId });
    
    if (!result.success) {
      return false;
    }

    // Remove email sending - it's now handled in the backend
    return true;
  } catch (error) {
    console.error('Error canceling meal signup:', error);
    return false;
  }
};

// Replace getMealSlotById function:
export const getMealSlotById = async (registryId: string, slotId: string): Promise<MealSlot | null> => {
  try {
    const result = await makeApiCall('getMealSlotById', { registryId, slotId });
    
    if (!result.success) {
      throw new Error('Failed to get meal slot');
    }
    
    return result.slot;
  } catch (error) {
    console.error('Error getting meal slot:', error);
    throw error;
  }
};
// Replace all these functions:

export const createRegistry = async (registryData: RegistryData): Promise<{ registryId: string }> => {
  try {
    const result = await makeApiCall('createRegistry', { registryData });
    
    if (!result.success) {
      throw new Error('Registry creation failed');
    }

    // Remove the email sending - it's now handled in the backend
    return { registryId: result.registryId };
  } catch (error) {
    console.error('Error creating registry:', error);
    throw error;
  }
};

export const getRegistryPublic = async (registryId: string): Promise<any> => {
  try {
    const result = await makeApiCall('getRegistryPublic', { registryId });
    
    if (!result.success) {
      if (result.error === 'Registry not found') {
        return null;
      }
      throw new Error('Failed to get registry');
    }
    
    return result.registry;
  } catch (error) {
    console.error('Error getting public registry:', error);
    throw error;
  }
};

// Keep the existing function for admin access - rename for clarity
export const getRegistryAdmin = async (registryId: string): Promise<any> => {
  try {
    const result = await makeApiCall('getRegistry', { registryId });
    
    if (!result.success) {
      if (result.error === 'Registry not found') {
        return null;
      }
      throw new Error('Failed to get registry');
    }
    
    return result.registry;
  } catch (error) {
    console.error('Error getting admin registry:', error);
    throw error;
  }
};

export const updateRegistry = async (registryId: string, registryData: Partial<RegistryData> & { [key: string]: any }): Promise<void> => {
  try {
    const result = await makeApiCall('updateRegistry', { registryId, registryData });
    
    if (!result.success) {
      throw new Error('Failed to update registry');
    }
  } catch (error) {
    console.error('Error updating registry:', error);
    throw error;
  }
};

export const deleteRegistry = async (registryId: string): Promise<void> => {
  try {
    const result = await makeApiCall('deleteRegistry', { registryId });
    
    if (!result.success) {
      throw new Error('Failed to delete registry');
    }
  } catch (error) {
    console.error('Error deleting registry:', error);
    throw error;
  }
};

export const getMealSlotsByRegistry = async (registryId: string): Promise<MealSlot[]> => {
  try {
    const result = await makeApiCall('getMealSlots', { registryId });
    
    if (!result.success) {
      throw new Error('Failed to get meal slots');
    }
    
    return result.slots || [];
  } catch (error) {
    console.error('Error getting meal slots:', error);
    throw error;
  }
};

export const createMealSlots = async (registryId: string, firstDeliveryDate: string, mealTags: MealTag[]): Promise<void> => {
  try {
    const result = await makeApiCall('createMealSlots', { registryId, firstDeliveryDate, mealTags });
    
    if (!result.success) {
      throw new Error('Failed to create meal slots');
    }
  } catch (error) {
    console.error('Error creating meal slots:', error);
    throw error;
  }
};

// Replace getMealSlotByDate function:
export const getMealSlotByDate = async (registryId: string, date: string): Promise<MealSlot | null> => {
  try {
    const result = await makeApiCall('getMealSlotByDate', { registryId, date });
    
    if (!result.success) {
      throw new Error('Failed to get meal slot by date');
    }
    
    return result.slot;
  } catch (error) {
    console.error('Error getting meal slot by date:', error);
    throw error;
  }
};

// Define interface for meal signup data
interface MealSignupData {
  registryId: string;
  date: string;
  name: string;
  email: string;
  phone: string;
  mealName: string;
  mealType?: 'restaurant' | 'homemade';
  timeWindow: {
    start: string;
    end: string;
  };
  gifterId: string;
  selectedFoodSubscription?: any;
}

// Replace saveMealSignup function:
export const saveMealSignup = async (signupData: MealSignupData): Promise<any> => {
  try {
    const result = await makeApiCall('saveMealSignup', { signupData });
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to save meal signup');
    }
    
    // Return the result so couponCode can be accessed
    return result;
  } catch (error) {
    console.error('Error saving meal signup:', error);
    throw error;
  }
};

// Replace updateRegistryStatus function:
export const updateRegistryStatus = async (registryId: string, status: string): Promise<void> => {
  try {
    const result = await makeApiCall('updateRegistryStatus', { registryId, status });
    
    if (!result.success) {
      throw new Error('Failed to update registry status');
    }
  } catch (error) {
    console.error('Error updating registry status:', error);
    throw error;
  }
};

// Replace getMealSignupsByRegistry function:
export const getMealSignupsByRegistry = async (registryId: string): Promise<any[]> => {
  try {
    const result = await makeApiCall('getMealSignupsByRegistry', { registryId });
    
    if (!result.success) {
      throw new Error('Failed to get meal signups');
    }
    
    return result.signups || [];
  } catch (error) {
    console.error('Error getting meal signups:', error);
    throw error;
  }
};

export const subscribeToNotifications = async (registryId: string, email: string, phone: string): Promise<void> => {
  try {
    const result = await makeApiCall('subscribeToNotifications', { 
      registryId, 
      email, 
      phone 
    });
    
    if (!result.success) {
      throw new Error('Failed to subscribe to notifications');
    }

    if (process.env.REACT_APP_SEND_EMAILS) {
      // sendSubscriptionEmail(registryId, email, phone);
    }
    
    console.log(`Successfully subscribed to notifications for registry ${registryId}`);
  } catch (error) {
    console.error('Error subscribing to notifications:', error);
    throw error;
  }
};